import { Link } from "react-router-dom";
import { Lightning } from "@phosphor-icons/react";
import BrandLogo from "@/components/BrandLogo";

const Privacy = () => (
  <div className="min-h-screen bg-[#0A0D14] text-white noise">
    <header className="border-b border-white/10 sticky top-0 bg-[#0A0D14]/95 backdrop-blur-md z-20">
      <div className="max-w-4xl mx-auto flex items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-2" data-testid="privacy-logo">
          <BrandLogo height={52} />
        </Link>
        <Link to="/" className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] hover:text-white">← BACK</Link>
      </div>
    </header>

    <div className="max-w-3xl mx-auto px-6 py-16">
      <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-3">— LEGAL</div>
      <h1 className="font-display text-5xl md:text-6xl tracking-tighter mb-8">Privacy Policy</h1>
      <div className="text-xs font-mono-k text-[#A1A1AA] mb-10">Last updated: February 2026</div>

      <div className="space-y-8 text-[#DDDDDD] font-sans leading-relaxed">
        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">1. Who we are</h2>
          <p>Korporate ("we", "us") provides a link-in-bio, shop, funnel, wallet, and AI marketing tool at korporatelinks.com. This policy explains what we collect, why, and how you control it.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">2. Information we collect</h2>
          <ul className="list-disc list-inside space-y-2">
            <li><strong>Account data</strong>: email, password hash (bcrypt), display name, username slug, avatar URL, bio, wallet address.</li>
            <li><strong>Google sign-in</strong>: if you use "Continue with Google", we receive your email, name, and profile picture from Google via Emergent-managed OAuth.</li>
            <li><strong>Content you create</strong>: bio links, products, leads, funnels, campaigns, AI conversations.</li>
            <li><strong>Payment data</strong>: PayPal order/subscription IDs, transaction amounts. We never see or store your card number — PayPal handles that end-to-end.</li>
            <li><strong>Analytics</strong>: click counts on your bio links (aggregated), lead form submissions, page views. Anonymised.</li>
            <li><strong>2FA data</strong>: your TOTP secret (encrypted at rest) and bcrypt hashes of recovery codes.</li>
            <li><strong>Push tokens</strong>: web-push endpoint + p256dh/auth keys tied to your device, only if you grant notification permission.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">3. How we use it</h2>
          <ul className="list-disc list-inside space-y-2">
            <li>Run the service — show your bio page, process payments, credit your wallet, deliver AI responses.</li>
            <li>Notify you — receipts, subscription renewals, referral commissions, lead captures (email + optional push).</li>
            <li>Secure your account — 2FA, password reset, login alerts, fraud prevention.</li>
            <li>Improve Korporate — usage analytics in aggregate, never sold to third parties.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">4. Third parties we share with</h2>
          <ul className="list-disc list-inside space-y-2">
            <li><strong>PayPal</strong> — payment processing (subscriptions, one-time orders, payouts).</li>
            <li><strong>Anthropic (via Emergent)</strong> — powers the AI Copilot; prompts you send are processed by Claude to generate responses.</li>
            <li><strong>Resend</strong> — sends transactional emails (verification, receipts, renewals).</li>
            <li><strong>Emergent Auth</strong> — Google OAuth broker.</li>
          </ul>
          <p className="mt-3">We do not sell your data. We do not run third-party ad trackers on your dashboard.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">5. Your rights</h2>
          <ul className="list-disc list-inside space-y-2">
            <li>Access, edit or delete your data from Settings at any time.</li>
            <li>Export a copy — email us and we'll send a machine-readable dump within 30 days.</li>
            <li>Delete your account permanently — email us; all your data is wiped within 30 days (payment records retained where required by law).</li>
            <li>Withdraw notification consent from Settings → Push Notifications.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">6. Data retention</h2>
          <p>We keep your data as long as your account is active. When you delete your account, we purge all personal data within 30 days. Anonymised aggregate analytics may be retained indefinitely.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">7. Security</h2>
          <p>Passwords are hashed with bcrypt (cost 12). 2FA is available and enforced on wallet withdrawals. Transport is TLS 1.2+. TOTP secrets are stored server-side; recovery codes are stored only as bcrypt hashes.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">8. Children</h2>
          <p>Korporate is not intended for anyone under 13. If we learn that a child has created an account, we'll delete it immediately.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">9. Contact</h2>
          <p>Questions, corrections, deletion requests: <span className="text-[#FACC15]">support@korporatelinks.com</span></p>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">10. Changes</h2>
          <p>We'll update this page and, for material changes, notify you by email. Continued use after changes means you accept the updated policy.</p>
        </section>
      </div>

      <div className="border-t border-white/10 mt-16 pt-8 text-xs font-mono-k tracking-widest-k text-[#A1A1AA] flex flex-wrap gap-6">
        <Link to="/" className="hover:text-white">← HOME</Link>
        <Link to="/terms" className="hover:text-white">TERMS</Link>
        <a href="mailto:support@korporatelinks.com" className="hover:text-white">CONTACT</a>
      </div>
    </div>
  </div>
);

export default Privacy;
